import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import SendIcon from '@mui/icons-material/Send';
import api from '../../../utils/functions/api';
import { showAlert } from '../../../utils/functions/globalDialog';
import { CATEGORY_OPTIONS, PRIORITY_OPTIONS, AUDIENCE_OPTIONS } from './constants';

const apiBase = process.env.REACT_APP_API_URL;
const MAX_UPLOAD_MB = 20; // ตรงกับ MAX_ATTACHMENT_MB ใน announcement.routes.js ฝั่ง backend
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

// สีของการ์ดเลือกระดับความสำคัญ (PRIORITY_OPTIONS.color เป็นชื่อสี MUI ใช้กับ Chip)
const PRIORITY_HEX = { normal: '#6b7280', important: '#f59e0b', critical: '#ef4444' };

const emptyForm = {
  title: '',
  body: '',
  category: 'general',
  priority: 'normal',
  audience_type: 'all',
  departments: [],
  roles: [],
  end_date: '',
  pinned: false,
  attachments: [],
};

const toDateInput = (v) => {
  if (!v) return '';
  const d = new Date(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const idOf = (x) => x?._id || x?.id || x;

const fileIcon = (url = '') => {
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url)) return ImageOutlinedIcon;
  if (/\.pdf(\?|$)/i.test(url)) return PictureAsPdfOutlinedIcon;
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return MovieOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
};

function SectionLabel({ children, hint }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{children}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Stack>
  );
}

// การ์ดตัวเลือกแบบกดเลือก (ใช้ทั้งหมวดและระดับความสำคัญ)
function OptionCard({
  selected, color, label, hint, onClick,
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        position: 'relative',
        p: 1.5,
        borderRadius: 2.5,
        cursor: 'pointer',
        border: '1.5px solid',
        borderColor: selected ? color : 'divider',
        bgcolor: selected ? alpha(color, 0.07) : 'background.paper',
        transition: 'all 0.15s ease',
        outline: 'none',
        '&:hover': { borderColor: color },
        '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(color, 0.25)}` },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0,
        }}
        />
        <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: selected ? color : 'text.primary' }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, lineHeight: 1.4 }}>
        {hint}
      </Typography>
      {selected && (
        <CheckCircleIcon sx={{
          position: 'absolute', top: 8, right: 8, fontSize: 18, color,
        }}
        />
      )}
    </Box>
  );
}

// สร้าง/แก้ไขประกาศ — announcement = null คือสร้างใหม่
export default function AnnouncementFormDialog({
  open, announcement, departments, roles, onClose, onSaved,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (announcement) {
      setForm({
        title: announcement.title || '',
        body: announcement.body || '',
        category: announcement.category || 'general',
        priority: announcement.priority || 'normal',
        audience_type: announcement.audience_type || 'all',
        departments: (announcement.departments || []).map(idOf),
        roles: (announcement.roles || []).map(idOf),
        end_date: toDateInput(announcement.end_at),
        pinned: !!announcement.pinned,
        attachments: announcement.attachments || [],
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, announcement]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const tooBig = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooBig.length) {
      showAlert(`ไฟล์ใหญ่เกิน ${MAX_UPLOAD_MB} MB: ${tooBig.map((f) => f.name).join(', ')}`);
    }
    const ok = files.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (!ok.length) return;

    setUploading(true);
    try {
      const uploaded = await Promise.all(ok.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post(`${apiBase}/announcement/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return { name: file.name, url: data.url };
      }));
      setForm((f) => ({ ...f, attachments: [...f.attachments, ...uploaded] }));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showAlert('กรุณากรอกหัวข้อประกาศ');
      return;
    }
    if (form.audience_type === 'department' && !form.departments.length) {
      showAlert('กรุณาเลือกแผนกอย่างน้อย 1 แผนก');
      return;
    }
    if (form.audience_type === 'role' && !form.roles.length) {
      showAlert('กรุณาเลือกบทบาทอย่างน้อย 1 บทบาท');
      return;
    }

    const { end_date: endDate, ...rest } = form;
    const payload = {
      ...rest,
      title: form.title.trim(),
      departments: form.audience_type === 'department' ? form.departments : [],
      roles: form.audience_type === 'role' ? form.roles : [],
      // หมดอายุสิ้นวันที่เลือก (เวลาเครื่องผู้ใช้)
      end_at: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null,
    };

    setSaving(true);
    try {
      if (announcement) {
        await api.put(`${apiBase}/announcement/${idOf(announcement)}`, payload);
      } else {
        await api.post(`${apiBase}/announcement`, payload);
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || uploading;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      {/* หัว modal */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            background: 'linear-gradient(135deg, rgb(var(--color-primary-450)) 0%, rgb(var(--color-primary-500)) 60%, rgb(var(--color-primary-600)) 100%)',
            boxShadow: '0 4px 12px rgb(var(--color-primary-500) / 0.3)',
            flexShrink: 0,
          }}
        >
          <CampaignIcon fontSize="small" />
        </Box>
        <Box flexGrow={1}>
          <Typography fontWeight={700} fontSize="1.1rem" lineHeight={1.2}>
            {announcement ? 'แก้ไขประกาศ' : 'สร้างประกาศ'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            พนักงานจะเห็นเป็น popup ทุกครั้งที่เปิดเว็บ
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small"><CloseIcon /></IconButton>
      </Stack>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={3}>
          {/* หมวด */}
          <Box>
            <SectionLabel>หมวดประกาศ</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25 }}>
              {CATEGORY_OPTIONS.map((c) => (
                <OptionCard
                  key={c.value}
                  selected={form.category === c.value}
                  color={c.color}
                  label={c.label}
                  hint={c.hint}
                  onClick={() => set('category')(c.value)}
                />
              ))}
            </Box>
          </Box>

          {/* เนื้อหา */}
          <Box>
            <SectionLabel>เนื้อหา</SectionLabel>
            <Stack spacing={1.5}>
              <TextField
                placeholder="หัวข้อประกาศ"
                value={form.title}
                onChange={(e) => set('title')(e.target.value)}
                fullWidth
                inputProps={{ style: { fontWeight: 600 } }}
              />
              <TextField
                placeholder="รายละเอียดประกาศ…"
                value={form.body}
                onChange={(e) => set('body')(e.target.value)}
                multiline
                minRows={5}
                fullWidth
              />
            </Stack>
          </Box>

          {/* ไฟล์แนบ */}
          <Box>
            <SectionLabel hint={`รูป, PDF, วิดีโอ, เอกสาร Office จะแสดงในประกาศเลย · ไม่เกินไฟล์ละ ${MAX_UPLOAD_MB} MB`}>
              ไฟล์แนบ
            </SectionLabel>
            <Box
              component="label"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (!uploading) uploadFiles(e.dataTransfer.files);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 2.5,
                borderRadius: 2.5,
                border: '1.5px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                bgcolor: dragOver ? 'rgb(var(--color-primary-500) / 0.05)' : 'rgb(var(--color-primary-500) / 0.015)',
                cursor: uploading ? 'progress' : 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgb(var(--color-primary-500) / 0.04)' },
              }}
            >
              {uploading
                ? <CircularProgress size={26} />
                : <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: 'primary.main' }} />}
              <Typography variant="body2" fontWeight={600}>
                {uploading ? 'กำลังอัปโหลด…' : 'คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวางที่นี่'}
              </Typography>
              <input
                type="file"
                hidden
                multiple
                disabled={uploading}
                onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
              />
            </Box>
            {form.attachments.length > 0 && (
              <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                {form.attachments.map((a, i) => {
                  const Icon = fileIcon(a.url);
                  return (
                    <Stack
                      key={a.url}
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{
                        px: 1.5, py: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Icon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography
                        variant="body2"
                        component="a"
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        noWrap
                        sx={{ flexGrow: 1, color: 'text.primary', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}
                      >
                        {a.name || `ไฟล์ ${i + 1}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => set('attachments')(form.attachments.filter((x) => x.url !== a.url))}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>

          {/* ระดับความสำคัญ */}
          <Box>
            <SectionLabel>ระดับความสำคัญ</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25 }}>
              {PRIORITY_OPTIONS.map((p) => (
                <OptionCard
                  key={p.value}
                  selected={form.priority === p.value}
                  color={PRIORITY_HEX[p.value]}
                  label={p.label}
                  hint={p.hint}
                  onClick={() => set('priority')(p.value)}
                />
              ))}
            </Box>
          </Box>

          {/* ส่งถึง */}
          <Box>
            <SectionLabel>ส่งถึง</SectionLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={form.audience_type}
              onChange={(_e, v) => v && set('audience_type')(v)}
              sx={{ '& .MuiToggleButton-root': { px: 2, fontWeight: 600 } }}
            >
              {AUDIENCE_OPTIONS.map((a) => (
                <ToggleButton key={a.value} value={a.value}>{a.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {form.audience_type === 'department' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.25 }}
                options={departments}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => idOf(o) === idOf(v)}
                value={departments.filter((d) => form.departments.includes(idOf(d)))}
                onChange={(_e, v) => set('departments')(v.map(idOf))}
                renderInput={(params) => <TextField {...params} placeholder="เลือกแผนก" />}
              />
            )}
            {form.audience_type === 'role' && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1.25 }}
                options={roles}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => idOf(o) === idOf(v)}
                value={roles.filter((r) => form.roles.includes(idOf(r)))}
                onChange={(_e, v) => set('roles')(v.map(idOf))}
                renderInput={(params) => <TextField {...params} placeholder="เลือกบทบาท" />}
              />
            )}
          </Box>

          {/* ตั้งค่าเพิ่มเติม */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                p: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider',
              }}
            >
              <EventOutlinedIcon color="action" />
              <Box flexGrow={1}>
                <Typography variant="body2" fontWeight={600}>วันหมดอายุ</Typography>
                <Typography variant="caption" color="text.secondary">เว้นว่าง = ไม่มีวันหมดอายุ</Typography>
              </Box>
              <TextField
                type="date"
                size="small"
                value={form.end_date}
                onChange={(e) => set('end_date')(e.target.value)}
                sx={{ width: 160 }}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              onClick={() => set('pinned')(!form.pinned)}
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: form.pinned ? 'primary.main' : 'divider',
                bgcolor: form.pinned ? 'rgb(var(--color-primary-500) / 0.05)' : 'transparent',
                cursor: 'pointer',
              }}
            >
              <PushPinOutlinedIcon color={form.pinned ? 'primary' : 'action'} />
              <Box flexGrow={1}>
                <Typography variant="body2" fontWeight={600}>ปักหมุด</Typography>
                <Typography variant="caption" color="text.secondary">แสดงไว้บนสุดของรายการประกาศ</Typography>
              </Box>
              <Switch checked={form.pinned} onClick={(e) => e.stopPropagation()} onChange={(e) => set('pinned')(e.target.checked)} />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving} color="inherit">ยกเลิก</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ px: 3, boxShadow: '0 4px 12px rgb(var(--color-primary-500) / 0.3)' }}
        >
          {announcement ? 'บันทึก' : 'ประกาศ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
